import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { allottedTimeFactory } from "@/components/Provider/ScheduleAvailability/constants";
import { AptihealthIcon } from "@/component-library/Icons/components/AptihealthIcon";
import { BhsIcon } from "@/component-library/Icons/components/BhsIcon";
import Button from "@/components/UI/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import images from "@/utils/images";
import { IntakeIcon } from "@/component-library/Icons/components/IntakeIcon";
import { Link, PageBanner, Text, TextColors, TextTypes } from "@/component-library";
import { PrescriberIcon } from "@/component-library/Icons/components/PrescriberIcon";
import ScheduleDialog from "@/components/Patient/CancellationPolicy/ScheduleDialog";
import { ViewTitle } from "@/features/dashboard/components/ViewTitle";

import "../../styles.scss";
import { buildCallDate, buildCallTimeInfo, buildCallTitle } from "../../helpers/call-meta";

export function ScheduleWidget({ profile, scheduleList }) {
  const history = useHistory();
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [showRescheduleModal, setShowRescheduleModal] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    setScheduledCalls(
      scheduleList
        .filter((call) => !Object.hasOwn(call, "is_instant") || !call["is_instant"])
        .slice(0, 2),
    );
  }, [scheduleList]);

  const rescheduleCall = (callId, providerId, allottedTime) =>
    history.push(
      "/app/schedule/assign?mode=edit&provider_id=" +
        providerId +
        "&call_id=" +
        callId +
        "&allotted_time=" +
        allottedTime,
    );

  const getCallIcon = (providerRole) => {
    switch (providerRole) {
      case "BEHAVIORAL":
        return (
          <BhsIcon
            className="IconAptiNavy"
            iconSize={ICON_SIZES.LARGE}
            iconType={ICON_TYPES.CIRCLE}
          />
        );

      case "BEHAVIORAL_INTAKE":
        return (
          <IntakeIcon
            className="IconAptiNavy"
            iconSize={ICON_SIZES.LARGE}
            iconType={ICON_TYPES.CIRCLE}
          />
        );

      case "PRESCRIBE":
        return (
          <PrescriberIcon
            className="IconAptiNavy"
            iconSize={ICON_SIZES.LARGE}
            iconType={ICON_TYPES.CIRCLE}
          />
        );

      default:
        return (
          <AptihealthIcon
            className="IconAptiNavy"
            iconSize={ICON_SIZES.LARGE}
            iconType={ICON_TYPES.CIRCLE}
          />
        );
    }
  };

  const shouldAllowScheduling = (profile) => {
    return (
      profile &&
      profile.assessment_status &&
      profile.status !== "INACTIVE" &&
      !profile.is_wait_listed &&
      !profile.is_scheduling_restricted
    );
  };

  const renderNoCalls = (allowScheduling) => {
    return (
      <div className="tw:m-auto tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-10">
        <div className="fs-22 tw:font-bold">No Upcoming Sessions</div>
        <div className="txt-gry mb-md-3 mb-1">
          Future sessions with your aptihealth care team will appear here.
        </div>

        {allowScheduling && (
          <Button
            className="Btn Btn--pri Btn-xs Btn-xs-2 Btn-rounded Btn--otl-pri Btn-transparent tw:font-bold"
            onClick={() => history.push("/app/schedule/assign?mode=new")}
            style={{ padding: "1px 10px", textDecoration: "none" }}
          >
            <div className="txt-white d-flex">
              <img alt="" src={images("./icons/common/schedule/schedule-pri-none.svg")} />
              <div className="ml-1 mt-md-1 mt-0">Schedule</div>
            </div>
          </Button>
        )}
      </div>
    );
  };

  const getAllottedTime = (allotted_time, event_type) => {
    return allotted_time ? allotted_time : allottedTimeFactory(event_type);
  };

  const renderCalls = (scheduledCalls, shouldAllowRescheduling) => {
    return (
      <ul>
        {scheduledCalls.map((call) => {
          const { allotted_time, callId, event_type, provider_name, provider_role, timestamp } =
            call;
          const allottedTime = getAllottedTime(allotted_time, event_type);

          return (
            <li
              className="my-lg-0 my-2 px-lg-4 px-2 tw:[&:not(:last-child)>div]:border-b tw:[&:not(:last-child)>div]:border-stroke-quaternary"
              key={callId}
            >
              <div className="tw:flex tw:py-5">
                <div className="pr-2">{getCallIcon(provider_role)}</div>
                <div>
                  <div className="fs-16 tw:font-bold">
                    {buildCallTitle(provider_role, event_type)} with{" "}
                    <span style={{ whiteSpace: "nowrap" }}>{provider_name}</span>
                  </div>

                  <div className="fs-14 txt-gry">
                    {buildCallDate(timestamp)}
                    {window.innerWidth > 320 ? " · " : " "}
                    <span style={{ whiteSpace: "nowrap" }}>
                      {buildCallTimeInfo(timestamp, allottedTime)}
                    </span>

                    {window.innerWidth > 400 ? " · " : " "}

                    <span style={{ whiteSpace: "nowrap" }}>{allottedTime} minutes</span>
                  </div>

                  <div className="d-flex mt-lg-2 mt-0">
                    {shouldAllowRescheduling && (
                      <Button
                        className="Btn-link txt-pri tw:font-bold"
                        onClick={() => {
                          setShowRescheduleModal(true);
                          setSelectedCall(call);
                        }}
                        style={{
                          margin: 0,
                          textDecoration: "none",
                        }}
                      >
                        <div className="d-flex">
                          <img
                            alt=""
                            src={images("./icons/common/schedule/schedule-pri-none.svg")}
                          />
                          <div className="ml-1 mt-1">Reschedule</div>
                        </div>
                      </Button>
                    )}
                  </div>

                  {showRescheduleModal && (
                    <ScheduleDialog
                      activeScheduleDetails={selectedCall}
                      cancelHandler={() => {
                        setShowRescheduleModal(false);
                        setSelectedCall(null);
                      }}
                      submitHandler={() =>
                        rescheduleCall(
                          selectedCall.callId,
                          selectedCall.provider_username,
                          getAllottedTime(selectedCall.allotted_time, selectedCall.event_type),
                        )
                      }
                      type="reschedule"
                    />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const restrictSchedulingBanner = profile?.is_scheduling_restricted && (
    <PageBanner
      className="ScheduleRestrictWidget d-flex flex-column margin-center"
      {...getAutomationDataAttr("member-home-scheduling-restricted")}
    >
      <Text className="align-self-baseline" color={TextColors.black} type={TextTypes.label}>
        Your scheduling options have changed
      </Text>

      <Text className="align-self-baseline" color={TextColors.black} type={TextTypes.paragraph}>
        To schedule/reschedule therapy sessions, contact your provider. To schedule/reschedule
        medication management sessions, contact{" "}
        <Link className="apti-green-v2" href="mailto:support@aptihealth.com">
          support@aptihealth.com
        </Link>
      </Text>
    </PageBanner>
  );

  return (
    <div
      className="tw:grid tw:grid-rows-[min-content_1fr] tw:gap-4"
      {...getAutomationDataAttr("patient-home-schedule-widget")}
    >
      <ViewTitle as="h2" size={3}>
        Schedule
      </ViewTitle>

      {restrictSchedulingBanner}

      <Card2 className="tw:flex-col">
        {scheduledCalls &&
          scheduledCalls.length === 0 &&
          renderNoCalls(shouldAllowScheduling(profile))}

        {scheduledCalls &&
          scheduledCalls.length !== 0 &&
          renderCalls(scheduledCalls, profile && !profile.is_scheduling_restricted)}
      </Card2>
    </div>
  );
}
